import {isFeatureEnabled} from '@github-ui/feature-flags'
import {commitSetLabelsForLabelableMutation} from '@github-ui/item-picker/commitSetLabelsForLabelableMutation'
import {ERRORS} from '@github-ui/item-picker-base/constants/errors'
import {LabelPicker, type LabelPickerLabel} from '@github-ui/item-picker-label-relay'
import {type Label, LabelsList as Labels} from '@github-ui/labels-list'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {useAnalytics} from '@github-ui/use-analytics'
import type {SxProp} from '@primer/styled-react'
import {useCallback, useMemo} from 'react'
import {graphql, useFragment, usePaginationFragment, useRelayEnvironment} from 'react-relay/hooks'

import {LABELS} from '../../constants/labels'
import {TEST_IDS} from '../../constants/test-ids'
import {commitRemoveLabelsFromLabelable} from '../../mutations/remove-labels-from-labelable'
import {getMostUsedLabels, recordLabelsUsage} from '../../utils/favorite-labels-storage'
import {ReadonlySectionHeader} from '../ReadonlySectionHeader'
import {Section} from '../Section'
import {SectionHeader} from '../SectionHeader'
import type {LabelsSectionAssignedLabels$key} from './__generated__/LabelsSectionAssignedLabels.graphql'
import type {LabelsSectionAssignedLabelsQuery} from './__generated__/LabelsSectionAssignedLabelsQuery.graphql'
import type {LabelsSectionFragment$key} from './__generated__/LabelsSectionFragment.graphql'

const ReadonlyLabelsSectionHeader = () => <ReadonlySectionHeader title={LABELS.sectionTitles.labels} />

type LabelsSectionProps = {
  sectionHeader: React.ReactElement
  onDelete?: (labelId: string) => void
  labels: Label[]
} & SxProp
const LabelsSection = ({sectionHeader, onDelete, labels}: LabelsSectionProps) => {
  return (
    <Section
      id={`sidebar-labels-section`}
      sectionHeader={sectionHeader}
      emptyText={labels.length > 0 ? undefined : LABELS.emptySections.labels}
    >
      <Labels labels={labels} onDelete={onDelete} testId={TEST_IDS.issueLabels} />
    </Section>
  )
}

type CreateIssueLabelsSectionProps = {
  repo: string
  owner: string
  readonly: boolean
  labels: LabelPickerLabel[]
  onSelectionChange: (labels: LabelPickerLabel[]) => void
}
export const CreateIssueLabelsSection = ({
  repo,
  owner,
  readonly,
  labels,
  onSelectionChange,
  ...sharedConfigProps
}: CreateIssueLabelsSectionProps) => {
  const sectionHeader = readonly ? (
    <ReadonlyLabelsSectionHeader />
  ) : (
    <LabelPicker
      repo={repo}
      owner={owner}
      readonly={readonly}
      canCreateLabel={!readonly}
      labels={labels}
      onSelectionChange={onSelectionChange}
      showNoMatchItem
      anchorElement={(anchorProps, ref) => (
        <SectionHeader title={LABELS.sectionTitles.labels} buttonProps={anchorProps} ref={ref} />
      )}
      {...sharedConfigProps}
    />
  )
  return <LabelsSection sectionHeader={sectionHeader} labels={labels} />
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const labelsSectionFragment = graphql`
  fragment LabelsSectionFragment on Issue {
    id
    title
    body
    repository {
      name
      owner {
        login
      }
      isArchived
    }
    ...LabelsSectionAssignedLabels
    viewerCanLabel
  }
`

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const LabelsSectionAssignedLabelsFragment = graphql`
  fragment LabelsSectionAssignedLabels on Labelable
  @argumentDefinitions(cursor: {type: "String"}, assignedLabelPageSize: {type: "Int", defaultValue: 100})
  @refetchable(queryName: "LabelsSectionAssignedLabelsQuery") {
    labels(first: $assignedLabelPageSize, after: $cursor, orderBy: {field: NAME, direction: ASC})
      @connection(key: "MetadataSectionAssignedLabels_labels") {
      edges {
        node {
          id
          color
          name
          nameHTML
          description
          url
        }
      }
    }
  }
`

export type EditIssueLabelsSectionProps = {
  onIssueUpdate?: () => void
  issue: LabelsSectionFragment$key
  insideSidePanel?: boolean
  readonly: boolean
}

export function EditIssueLabelsSection({issue, onIssueUpdate, insideSidePanel, readonly}: EditIssueLabelsSectionProps) {
  const {sendAnalyticsEvent} = useAnalytics()
  const data = useFragment(labelsSectionFragment, issue)
  const {
    repository: {
      owner: {login: owner},
      name: repo,
      isArchived: isRepositoryArchived,
    },
  } = data

  const {data: labelDataRaw} = usePaginationFragment<LabelsSectionAssignedLabelsQuery, LabelsSectionAssignedLabels$key>(
    LabelsSectionAssignedLabelsFragment,
    data,
  )

  const labelData = labelDataRaw

  const {id: issueId, viewerCanLabel} = data

  const labels = useMemo(() => labelData.labels?.edges?.flatMap(e => (e?.node ? [e?.node] : [])) || [], [labelData])

  const environment = useRelayEnvironment()
  const report_sidebar_interactions = isFeatureEnabled('issues_report_sidebar_interactions')
  const storeFavoriteLabels = isFeatureEnabled('issues_react_favorite_labels')

  const readonlyLabel = readonly || !viewerCanLabel || isRepositoryArchived

  const {addToast} = useToastContext()

  const onSelectionChanged = useCallback(
    (selectedLabels: LabelPickerLabel[]) => {
      commitSetLabelsForLabelableMutation({
        environment,
        input: {labelableId: issueId, labels: selectedLabels, labelableTypeName: 'Issue'},
        onError: () =>
          // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
          addToast({
            type: 'error',
            message: ERRORS.couldNotUpdateLabels,
          }),
        onCompleted: () => {
          if (storeFavoriteLabels && selectedLabels.length > 0) {
            recordLabelsUsage(
              `${owner}/${repo}`,
              selectedLabels.map(label => label.id),
            )
          }

          onIssueUpdate?.()
        },
      })
    },
    [addToast, environment, issueId, onIssueUpdate, owner, repo, storeFavoriteLabels],
  )

  const isRemoveLabelEnabled = isFeatureEnabled('issues_react_remove_labels_oneclick')
  const onDeleteLabelCallback = useCallback(
    (labelId: string) => {
      commitRemoveLabelsFromLabelable({
        environment,
        input: {
          labelableId: issueId,
          labelIds: [labelId],
        },
      })
    },
    [environment, issueId],
  )

  const labelSectionHeader = useMemo(() => {
    if (readonlyLabel) {
      return <ReadonlyLabelsSectionHeader />
    }

    const favoriteLabelIds = storeFavoriteLabels
      ? getMostUsedLabels(`${owner}/${repo}`).map(entry => entry.labelId)
      : []

    return (
      <LabelPicker
        repo={repo}
        owner={owner}
        readonly={readonlyLabel}
        labels={labels}
        canCreateLabel={!readonlyLabel}
        favoriteLabelIds={favoriteLabelIds}
        onSelectionChange={onSelectionChanged}
        anchorElement={(anchorProps, ref) => (
          <SectionHeader
            title={LABELS.sectionTitles.labels}
            buttonProps={{
              ...anchorProps,
              onClick: e => {
                if (report_sidebar_interactions) {
                  sendAnalyticsEvent('analytics.click', 'ISSUE_SIDEBAR_LABELS_SECTION_EDIT_CLICK')
                }
                anchorProps.onClick?.(e)
              },
            }}
            ref={ref}
          />
        )}
        insidePortal={insideSidePanel}
        showNoMatchItem
      />
    )
  }, [
    readonlyLabel,
    storeFavoriteLabels,
    owner,
    repo,
    labels,
    onSelectionChanged,
    insideSidePanel,
    report_sidebar_interactions,
    sendAnalyticsEvent,
  ])

  return (
    <LabelsSection
      sectionHeader={labelSectionHeader}
      onDelete={isRemoveLabelEnabled && !readonlyLabel ? onDeleteLabelCallback : undefined}
      labels={labels}
    />
  )
}
